import {
  ComponentRef,
  Directive,
  inject,
  Input,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';

import { AwaitSpinnerUiComponent } from '../await-spinner-ui/await-spinner-ui.component';

export interface tsAwaitSpinnerContext<T> {
  tsAwaitSpinner: T;
}

/**
 * A structural directive that conditionally includes a template when the expression
 * evaluates to any non-null value (including false, 0, etc),
 * and otherwise shows an `<ion-spinner>`.
 *
 * A [shorthand form](guide/structural-directives#asterisk) of the directive,
 * `*tsAwaitSpinner="condition"` can be used.
 *
 * @example
 * ```
 * <div *tsAwaitSpinner="$products | async as products">You have {{ products.length}} products.</div>
 * ```
 */
@Directive({
  selector: '[tsAwaitSpinner]',
})
export class AwaitSpinnerDirective<T = unknown> {
  private templateRef =
    inject<TemplateRef<tsAwaitSpinnerContext<T>>>(TemplateRef);
  private viewContainerRef = inject(ViewContainerRef);

  private componentRef?: ComponentRef<AwaitSpinnerUiComponent<T>>;

  // We can't use OnInit here because this tsAwaitSpinner always rely on componentRef
  // and we need tsAwaitSpinner to always react to any changes
  @Input({ required: true })
  set tsAwaitSpinner(condition: T) {
    if (this.componentRef === undefined) {
      this.componentRef = this.viewContainerRef.createComponent(
        AwaitSpinnerUiComponent<T>,
      );
      this.componentRef.setInput('templateRef', this.templateRef);
    }

    if (condition !== null && condition !== undefined) {
      this.componentRef.setInput('isShowSpinner', false);
      this.componentRef.setInput('context', { tsAwaitSpinner: condition });
    } else {
      this.componentRef.setInput('isShowSpinner', true);
    }
  }

  /**
   * Assert the correct type of the expression bound to the `ngIf` input within the template.
   *
   * The presence of this static field is a signal to the Ivy template type check compiler that
   * when the `NgIf` structural directive renders its template, the type of the expression bound
   * to `ngIf` should be narrowed in some way. For `NgIf`, the binding expression itself is used to
   * narrow its type, which allows the strictNullChecks feature of TypeScript to work with `NgIf`.
   */
  static ngTemplateGuard_tsAwaitSpinner: 'binding';

  /**
   * Asserts the correct type of the context for the template that `NgIf` will render.
   *
   * The presence of this method is a signal to the Ivy template type-check compiler that the
   * `NgIf` structural directive renders its template with a specific context type.
   */
  static ngTemplateContextGuard<T>(
    dir: AwaitSpinnerDirective<T>,
    ctx: tsAwaitSpinnerContext<T>,
  ): ctx is tsAwaitSpinnerContext<
    Exclude<T, false | 0 | '' | null | undefined>
  > {
    return true;
  }
}
